import { Eyebrow } from '../brand';
import { PHOTOS, VISION_CUSTOM_EXAMPLES, VISION_SECTORS, VISION_SOFTWARE, WHY_VISION } from '../catalog';
import { useHref, useLang } from '../lang';
import { Pic } from '../pic';
import { demoHref } from '../routes';
import { Faq } from '../sections/Faq';
import { Closer, HowYouStart, LineHero, SectionHead, Subnav, Why } from '../sections/Shared';
import { Software } from '../sections/Software';

const SUBNAV = [
  { href: '#software', key: 'nav.software' },
  { href: '#examples', key: 'nav.examples' },
  { href: '#privacy', key: 'nav.privacyData' },
  { href: '#start', key: 'nav.start' },
  { href: '#faq', key: 'nav.faq' },
] as const;

export function Vision() {
  return (
    <>
      <LineHero line="vision" />
      <Subnav items={SUBNAV} />
      <Software prefix="vsoft" features={VISION_SOFTWARE} />
      <Custom />
      <Applications />
      <Why prefix="vwhy" ids={WHY_VISION} tone="soft" />
      <Privacy />
      <HowYouStart line="vision" prefix="vstart" photo={PHOTOS.visionTrial} specs={['cameras', 'server', 'duration']} />
      <Faq prefix="vfaq" />
      <Closer titleKey="close.vision.title" line="vision" />
    </>
  );
}

function Applications() {
  const { t } = useLang();
  return (
    <section className="band apps" id="examples" aria-labelledby="vapp-title">
      <div className="frame">
        <SectionHead id="vapp-title" prefix="vapp" />
        <ul className="vision-grid" role="list">
          {VISION_SECTORS.map((s) => (
            <li key={s.id} data-reveal>
              <figure>
                <Pic photo={s.photo} alt={t(`vapp.${s.id}.alt`)} />
              </figure>
              <h3 className="h3">{t(`vapp.${s.id}`)}</h3>
              <p>{t(`vapp.${s.id}.d`)}</p>
              <dl className="examples">
                {[1, 2, 3].map((n) => (
                  <div key={n}>
                    <dt>{t(`vapp.${s.id}.${n}`)}</dt>
                    <dd>{t(`vapp.${s.id}.${n}.d`)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Custom() {
  const { t } = useLang();
  const href = useHref();
  return (
    <section className="band band-soft custom" id="custom" aria-labelledby="vcustom-title">
      <div className="frame">
        <div className="custom-grid">
          <div className="custom-copy" data-reveal>
            <Eyebrow k="vcustom.eyebrow" />
            <h2 id="vcustom-title" className="h2">
              {t('vcustom.title')}
            </h2>
            <p className="lead">{t('vcustom.lead')}</p>
            <a className="btn btn-primary" href={href(demoHref('vision'))}>
              {t('vcustom.cta')}
            </a>
          </div>
          <div className="custom-examples" data-reveal>
            <p className="mono">{t('vcustom.ex')}</p>
            <ul role="list">
              {VISION_CUSTOM_EXAMPLES.map((n) => (
                <li key={n}>{t(`vcustom.ex${n}`)}</li>
              ))}
            </ul>
          </div>
        </div>
        <ol className="steps steps-3 custom-steps">
          {[1, 2, 3].map((n) => (
            <li key={n} data-reveal>
              <span className="mono steps-n" aria-hidden="true">
                {n}
              </span>
              <h3 className="h4">{t(`vcustom.s${n}`)}</h3>
              <p>{t(`vcustom.s${n}.d`)}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function Privacy() {
  const { t } = useLang();
  return (
    <section className="band band-ink privacy-data" id="privacy" aria-labelledby="vpriv-title">
      <div className="frame">
        <SectionHead id="vpriv-title" prefix="vpriv" tone="paper" />
        <ul className="uses-grid" role="list">
          {[1, 2, 3, 4, 5].map((n) => (
            <li key={n} data-reveal>
              <h3 className="h4">{t(`vpriv.${n}`)}</h3>
              <p>{t(`vpriv.${n}.d`)}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
